/**
 * Sidebar order follows the daily CRM workflow:
 * overview → lead intake → assignment → follow-ups → pipeline views → buyers & inventory → reports → HR → settings.
 * Keys must stay in sync with backend ClientModuleAccessHelper.KnownKeys.
 */
export const CLIENT_MODULES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main KPI dashboard and widgets.', icon: 'fas fa-chart-line' },
  { key: 'leads', label: 'Leads', description: 'Lead list, capture, import, and lead detail.', icon: 'fas fa-user-plus' },
  { key: 'assignedLeads', label: 'Assigned leads', description: 'Assignments, calendar, pipeline filters, edit assignment.', icon: 'fas fa-tasks' },
  { key: 'pendingFollowUps', label: 'Pending follow-ups', description: 'Overdue and upcoming follow-up queue.', icon: 'fas fa-bell' },
  { key: 'kanban', label: 'Kanban pipeline', description: 'Board view for assigned leads.', icon: 'fas fa-columns' },
  { key: 'buyers', label: 'Buyers', description: 'Buyer CRM profiles and deals.', icon: 'fas fa-user-check' },
  { key: 'propertyListing', label: 'Property listing', description: 'Inventory, bookings, property detail.', icon: 'fas fa-building' },
  { key: 'builders', label: 'Builders / partners', description: 'Channel partners & builders.', icon: 'fas fa-handshake' },
  { key: 'reports', label: 'Reports', description: 'Reports & analytics.', icon: 'fas fa-chart-bar' },
  { key: 'marketingAnalytics', label: 'Marketing analytics', description: 'Meta campaign ROI, revenue analytics, and spend sync.', icon: 'fas fa-bullhorn' },
  { key: 'attendance', label: 'My attendance', description: 'Personal attendance.', icon: 'fas fa-calendar-check' },
  { key: 'teamAttendance', label: 'Team attendance', description: 'Admin team attendance overview.', icon: 'fas fa-users' },
  { key: 'settings', label: 'Workspace settings', description: 'Workspace rules, notification engine, and employee management.', icon: 'fas fa-cog' },
  { key: 'pipelineStages', label: 'Pipeline stages', description: 'Configure pipeline stages.', icon: 'fas fa-stream' },
  { key: 'notificationEngine', label: 'Notification engine', description: 'Scheduler settings, templates, queue, and delivery history.', icon: 'fas fa-bell' },
  { key: 'employees', label: 'Employees', description: 'Register & manage tenant users.', icon: 'fas fa-id-badge' },
  { key: 'quickSupport', label: 'Quick support', description: 'Support request form.', icon: 'fas fa-headset' },
  { key: 'branches', label: 'Branches', description: 'Organization locations and branch management.', icon: 'fas fa-code-branch' },
  { key: 'applicants', label: 'Applicants', description: 'Immigration applicant profiles.', icon: 'fas fa-user-graduate' },
  { key: 'cases', label: 'Cases', description: 'Visa cases and case pipeline.', icon: 'fas fa-folder-open' },
  { key: 'countries', label: 'Countries', description: 'Destination country catalog.', icon: 'fas fa-globe' },
  { key: 'visaTypes', label: 'Visa types', description: 'Visa programs per country.', icon: 'fas fa-passport' },
  { key: 'documentTemplates', label: 'Documents', description: 'Required document checklists.', icon: 'fas fa-file-alt' },
  { key: 'counselling', label: 'Counselling', description: 'Lead counselling sessions.', icon: 'fas fa-comments' },
  { key: 'appointments', label: 'Appointments', description: 'Biometric, embassy, interview.', icon: 'fas fa-calendar-alt' },
  { key: 'casePayments', label: 'Case payments', description: 'Consultation and embassy fees.', icon: 'fas fa-receipt' },
  { key: 'assignmentRules', label: 'Assignment rules', description: 'Lead auto-assignment by branch or country.', icon: 'fas fa-random' },
  { key: 'institutions', label: 'Institutions', description: 'Schools, colleges, and universities.', icon: 'fas fa-university' },
  { key: 'courses', label: 'Courses', description: 'Academic programs catalog.', icon: 'fas fa-book' },
  { key: 'students', label: 'Students', description: 'Student profiles from education leads.', icon: 'fas fa-user-graduate' },
  { key: 'admissions', label: 'Admissions', description: 'Admission applications and enrollment.', icon: 'fas fa-file-signature' },
  { key: 'educationCounselling', label: 'Education counselling', description: 'Education lead counselling sessions.', icon: 'fas fa-comments' },
  { key: 'scholarships', label: 'Scholarships', description: 'Financial aid programs.', icon: 'fas fa-award' },
  { key: 'exams', label: 'Exams', description: 'Academic exams and results.', icon: 'fas fa-clipboard-check' },
  { key: 'studentAttendance', label: 'Student attendance', description: 'Academic attendance (not HR).', icon: 'fas fa-user-check' },
  { key: 'placements', label: 'Placements', description: 'Job placements after enrollment.', icon: 'fas fa-briefcase' },
  { key: 'hrDepartments', label: 'HR departments', description: 'Organization departments and hierarchy.', icon: 'fas fa-sitemap', group: 'HR & Payroll' },
  { key: 'hrLeaveManagement', label: 'HR leave management', description: 'Leave balances and HR approval workflow.', icon: 'fas fa-umbrella-beach', group: 'HR & Payroll' },
  { key: 'hrHolidays', label: 'HR holidays', description: 'Holiday calendar for attendance and leave.', icon: 'fas fa-calendar-day', group: 'HR & Payroll' },
  { key: 'payroll', label: 'Payroll', description: 'Payroll runs and salary processing.', icon: 'fas fa-money-check-alt', group: 'HR & Payroll' },
  { key: 'payslips', label: 'Payslips', description: 'Employee self-service payslip access.', icon: 'fas fa-file-invoice', group: 'HR & Payroll' },
  { key: 'financeInvoices', label: 'Finance invoices', description: 'Cross-industry invoicing.', icon: 'fas fa-file-invoice-dollar', group: 'Finance' },
  { key: 'financePayments', label: 'Finance payments', description: 'Payment receipts and collections.', icon: 'fas fa-hand-holding-usd', group: 'Finance' },
  { key: 'financeExpenses', label: 'Finance expenses', description: 'Expense claims and approvals.', icon: 'fas fa-receipt', group: 'Finance' },
  { key: 'financeCommissions', label: 'Finance commissions', description: 'Sales commission accruals and payouts.', icon: 'fas fa-percentage', group: 'Finance' },
  { key: 'financeLedger', label: 'Finance ledger', description: 'General ledger and journal entries.', icon: 'fas fa-book', group: 'Finance' },
  { key: 'financeTax', label: 'Finance tax', description: 'GST/TDS tax rules.', icon: 'fas fa-landmark', group: 'Finance' },
  { key: 'financeReports', label: 'Finance reports', description: 'Finance analytics and reports.', icon: 'fas fa-chart-line', group: 'Finance' },
  { key: 'financeHrPack', label: 'Finance & HR pack', description: 'Meta-toggle for HR + Finance add-on modules.', icon: 'fas fa-box-open', group: 'Finance' },
  { key: 'socialMediaAutomation', label: 'Social media automation', description: 'AI social content generation and auto-publishing.', icon: 'fas fa-share-alt', group: 'Marketing' },
];

/**
 * Opt-in modules default OFF when absent from ModuleAccessJson — mirrors
 * RealEstateManagement.Service.Helpers.ClientModuleAccessHelper.DefaultOffKeys.
 */
export const DEFAULT_OFF_MODULE_KEYS = new Set([
  'hrDepartments',
  'hrLeaveManagement',
  'hrHolidays',
  'payroll',
  'payslips',
  'financeInvoices',
  'financePayments',
  'financeExpenses',
  'financeCommissions',
  'financeLedger',
  'financeTax',
  'financeReports',
  'kpi_payrollSummary',
  'kpi_expensePending',
  'kpi_revenueMtd',
  'kpi_commissionLiability',
  'kpi_feeOutstanding',
  'financeHrPack',
  'socialMediaAutomation',
]);

export function defaultModuleFlagEnabled(moduleKey) {
  return !DEFAULT_OFF_MODULE_KEYS.has(moduleKey);
}

/** Path prefix → module key (first match wins). Used when guarding routes. */
export const PATH_MODULE_RULES = [
  [/^\/dashboard(?:\/|$)/i, 'dashboard'],
  [/^\/$/i, 'dashboard'],
  [/^\/filteredLeads/i, 'leads'],
  [/^\/(?:leads|addlead|editlead|importLeads|printlead)(?:\/|$)/i, 'leads'],
  [/^\/editfbintegration/i, 'leads'],
  [/^\/(?:leadassigned|addleadsassigned|editleadassigned|filteredDataEmployee|filteredDashboarddata|filterdata)(?:\/|$)/i, 'assignedLeads'],
  [/^\/calendar(?:\/|$)/i, 'assignedLeads'],
  [/^\/(?:buyers|addbuyer|editbuyer)(?:\/|$)/i, 'buyers'],
  [/^\/kanban(?:\/|$)/i, 'kanban'],
  [/^\/pipeline-stages(?:\/|$)/i, 'pipelineStages'],
  [/^\/pendingfollowups(?:\/|$)/i, 'pendingFollowUps'],
  [/^\/(?:propertylisting|property\/|addproperty|editproperty)(?:\/|$)/i, 'propertyListing'],
  [/^\/bookings(?:\/|$)/i, 'propertyListing'],
  [/^\/(?:builders|addbuilder|editbuilder)(?:\/|$)/i, 'builders'],
  [/^\/reports(?:\/|$)/i, 'reports'],
  [/^\/analytics\/revenue(?:\/|$)/i, 'marketingAnalytics'],
  [/^\/social(?:\/|$)/i, 'socialMediaAutomation'],
  [/^\/attendance(?:\/|$)/i, 'attendance'],
  [/^\/team-attendance(?:\/|$)/i, 'teamAttendance'],
  [/^\/(?:registeruser|adduser|edituser)(?:\/|$)/i, 'employees'],
  [/^\/(?:branches|addbranch|editbranch)(?:\/|$)/i, 'branches'],
  [/^\/immigration\/applicants(?:\/add|\/edit\/[^/]+)?(?:\/|$)/i, 'applicants'],
  [/^\/immigration\/applicants\/[^/]+(?:\/|$)/i, 'applicants'],
  [/^\/immigration\/cases(?:\/|$)/i, 'cases'],
  [/^\/immigration\/countries(?:\/add|\/edit\/[^/]+)?(?:\/|$)/i, 'countries'],
  [/^\/immigration\/visa-types(?:\/|$)/i, 'visaTypes'],
  [/^\/immigration\/documents(?:\/|$)/i, 'documentTemplates'],
  [/^\/immigration\/counselling(?:\/|$)/i, 'counselling'],
  [/^\/immigration\/appointments(?:\/|$)/i, 'appointments'],
  [/^\/immigration\/payments(?:\/|$)/i, 'casePayments'],
  [/^\/immigration\/assignment-rules(?:\/|$)/i, 'assignmentRules'],
  [/^\/education\/students(?:\/|$)/i, 'students'],
  [/^\/education\/counselling(?:\/|$)/i, 'educationCounselling'],
  [/^\/education\/admissions(?:\/|$)/i, 'admissions'],
  [/^\/education\/admissions\/[^/]+(?:\/|$)/i, 'admissions'],
  [/^\/education\/fees(?:\/|$)/i, 'admissions'],
  [/^\/education\/institutions(?:\/|$)/i, 'institutions'],
  [/^\/education\/courses(?:\/|$)/i, 'courses'],
  [/^\/education\/scholarships(?:\/|$)/i, 'scholarships'],
  [/^\/education\/exams(?:\/|$)/i, 'exams'],
  [/^\/education\/student-attendance(?:\/|$)/i, 'studentAttendance'],
  [/^\/education\/placements(?:\/|$)/i, 'placements'],
  [/^\/education\/document-templates(?:\/|$)/i, 'admissions'],
  [/^\/hr\/departments(?:\/|$)/i, 'hrDepartments'],
  [/^\/hr\/designations(?:\/|$)/i, 'hrDepartments'],
  [/^\/hr\/employees(?:\/|$)/i, 'hrDepartments'],
  [/^\/hr\/holidays(?:\/|$)/i, 'hrHolidays'],
  [/^\/hr\/leave(?:\/|$)/i, 'hrLeaveManagement'],
  [/^\/finance\/payroll(?:\/|$)/i, 'payroll'],
  [/^\/finance\/salary-structures(?:\/|$)/i, 'payroll'],
  [/^\/finance\/expenses(?:\/|$)/i, 'financeExpenses'],
  [/^\/finance\/invoices(?:\/|$)/i, 'financeInvoices'],
  [/^\/finance\/payments(?:\/|$)/i, 'financePayments'],
  [/^\/finance\/ledger(?:\/|$)/i, 'financeLedger'],
  [/^\/finance\/commissions(?:\/|$)/i, 'financeCommissions'],
  [/^\/finance\/tax(?:\/|$)/i, 'financeTax'],
  [/^\/finance\/reports(?:\/|$)/i, 'financeReports'],
  [/^\/quicksupport(?:\/|$)/i, 'quickSupport'],
  [/^\/settings(?:\/|$)/i, 'settings'],
  [/^\/notification-engine(?:\/|$)/i, 'notificationEngine'],
];
